import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { api, ApiError } from '../api.js';
import type { LobbyChatMessage } from '../api.js';

/** The server keeps this many, and so does the panel. */
const KEPT = 100;

/** 26-10-30 14:05: when a line was said, here, on a 24-hour clock. */
function day(at: string): string {
  const d = new Date(at);
  const two = (n: number) => String(n).padStart(2, '0');
  return `${two(d.getFullYear() % 100)}-${two(d.getMonth() + 1)}-${two(d.getDate())}`
    + ` ${two(d.getHours())}:${two(d.getMinutes())}`;
}

/**
 * Talk on the main screen, before and between games. Read on opening and
 * then kept up by the lobby's stream; the newest line is at the bottom.
 */
export function LobbyChat() {
  const [messages, setMessages] = useState<LobbyChatMessage[]>([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scroller = useRef<HTMLDivElement>(null);

  const add = (m: LobbyChatMessage) => setMessages((current) => (
    current.some((c) => c.id === m.id) ? current : [...current, m].slice(-KEPT)
  ));

  // Merged with what the stream has brought meanwhile, which a read
  // begun before it can be missing.
  const reread = () => void api.lobbyChat().then((r) => setMessages((current) => {
    const known = new Set(r.messages.map((m) => m.id));
    return [...r.messages, ...current.filter((m) => !known.has(m.id))]
      .sort((a, b) => a.id - b.id).slice(-KEPT);
  })).catch(() => {});

  useEffect(() => {
    reread();
    const source = new EventSource('/api/lobby/chat/stream', { withCredentials: true });
    source.addEventListener('chat', (e) => add(JSON.parse((e as MessageEvent).data) as LobbyChatMessage));
    // Lines said while the stream was down are read again on reconnect.
    source.addEventListener('open', reread);
    return () => source.close();
  }, []);

  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const send = async (event: FormEvent) => {
    event.preventDefault();
    const said = text.trim();
    if (!said || sending) return;
    setSending(true);
    setError(null);
    try {
      add((await api.sendLobbyChat(said)).message);
      // Only what was sent: anything typed since stays.
      setText((current) => (current.trim() === said ? '' : current));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That could not be sent.');
    } finally {
      setSending(false);
    }
  };

  return (
    <aside className="lobby-chat stack" style={{ gap: '0.4rem' }}>
      <p className="eyebrow">Chat</p>
      <div className="log chat" role="log" ref={scroller}>
        {messages.length === 0 && <p className="muted">Nothing has been said yet.</p>}
        {messages.map((m) => (
          <p key={m.id} className="chat-line">
            <span className="muted">{day(m.at)}</span>{' '}
            <span className="chat-from">{m.fromName}:</span>{' '}{m.text}
          </p>
        ))}
      </div>
      <form className="chat-form" onSubmit={(e) => void send(e)}>
        <input
          className="chat-input"
          value={text}
          maxLength={127}
          onChange={(e) => setText(e.target.value)}
          aria-label="Message"
        />
        <button className="secondary tiny" type="submit" disabled={sending || !text.trim()}>
          Send
        </button>
      </form>
      {error && <p className="muted">{error}</p>}
    </aside>
  );
}
